import { Button } from "antd";
import { MessageBox } from "components/atoms/MessageBox";
import ProhibitedArea from "components/molecules/ProhibitedArea";
import { Link } from "react-router-dom";

export type BusinessAreaLoadFailure = "notFound" | "denied" | "failed";

interface IBusinessAreaLoadErrorProps {
    kind: BusinessAreaLoadFailure;
    // What could not be loaded, as the user knows it: "business area" or "schema".
    subject?: string;
    // The name (or id) it was looked for by.
    name?: string;
    // Where the list of them is, offered when it does not exist.
    listPath?: string;
    onRetry?: () => void;
}

// A page's business area (or schema) could not be loaded: it does not exist (or is not one of the
// user's), the user may not open it, or loading it failed — which a retry may fix.
const BusinessAreaLoadError = ({ kind, subject = "business area", name, listPath, onRetry }: IBusinessAreaLoadErrorProps) => {
    if (kind === "denied") {
        return <ProhibitedArea />;
    }
    const title = subject.charAt(0).toUpperCase() + subject.slice(1);
    if (kind === "notFound") {
        return (
            <MessageBox floating size="big" header={`${title} not found`}>
                <p data-testid="business-area-not-found">
                    {name ? `There is no ${subject} "${name}" that you have access to.` : `The ${subject} you are looking for does not exist, or you do not have access to it.`}
                </p>
                {listPath && <Link to={listPath}>Back to the list</Link>}
            </MessageBox>
        );
    }
    return (
        <MessageBox type="error" floating size="big" header={`The ${subject} could not be loaded`}>
            <p data-testid="business-area-load-failed">Something went wrong while loading it. Try again in a moment.</p>
            {onRetry && (
                <Button onClick={onRetry} data-testid="business-area-load-retry">
                    Try again
                </Button>
            )}
        </MessageBox>
    );
};

export default BusinessAreaLoadError;
